import { X, Monitor, Terminal, User, Clock, Globe, Wifi, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../utils/format'

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

export default function SessionDetailModal({ session, onClose, onTerminate, isTerminating }) {
  const { t } = useTranslation()
  if (!session) return null

  const isRemoteDisplay = session.sessionType === 'RemoteDisplay'
  const TypeIcon = isRemoteDisplay ? Monitor : Terminal

  const rows = [
    { icon: TypeIcon,  label: t('sessions.detail.type'),           value: isRemoteDisplay ? t('sessions.detail.typeRemote') : t('sessions.detail.typeSsh') },
    { icon: Monitor,   label: t('sessions.detail.host'),           value: session.hostName || session.hostId },
    { icon: User,      label: t('sessions.detail.user'),           value: session.userName || '—' },
    { icon: User,      label: t('sessions.detail.email'),          value: session.userEmail || '—' },
    { icon: Globe,     label: t('sessions.detail.sourceIp'),       value: session.sourceIp || '—' },
    { icon: Clock,     label: t('sessions.detail.startedAt'),      value: session.startedAt ? formatDateTime(session.startedAt, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—' },
    { icon: Clock,     label: t('sessions.detail.duration'),       value: formatDuration(session.durationSeconds) },
    { icon: Wifi,      label: t('sessions.detail.networkLatency'), value: session.latencyMs != null ? `${Math.round(session.latencyMs)} ms` : '—' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isRemoteDisplay ? 'bg-blue-100' : 'bg-green-100'}`}>
              <TypeIcon className={`w-5 h-5 ${isRemoteDisplay ? 'text-blue-600' : 'text-green-600'}`} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('sessions.detail.title')}</h2>
              <p className="text-xs text-gray-500">
                {isRemoteDisplay ? t('sessions.detail.remoteDisplayActive') : t('sessions.detail.sshActive')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          {rows.map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-start gap-3">
              <div className="mt-0.5 p-1.5 bg-gray-50 rounded-lg shrink-0">
                <Icon className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 leading-none mb-0.5">{label}</p>
                <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
              </div>
            </div>
          ))}

          {session.sessionId && (
            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-1.5 bg-gray-50 rounded-lg shrink-0">
                <Globe className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 leading-none mb-0.5">{t('sessions.detail.id')}</p>
                <p className="text-xs font-mono text-gray-500 truncate">{session.sessionId}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('sessions.detail.close')}
          </button>
          {session.canTerminate && (
            <button
              onClick={() => onTerminate(session.sessionId)}
              disabled={isTerminating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {isTerminating ? t('sessions.detail.terminating') : t('sessions.detail.terminate')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
